import { useEffect, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import {
  graceRemainingMs,
  isCancellable,
} from '@/db/application/whatsappMessages'
import { toZaDateKey, toZaTime } from '@/lib/datetime'
import { useDb } from '@/lib/dbContext'
import {
  WhatsAppScreen,
  type WhatsAppMessageRow,
} from '@/screens/WhatsAppScreen'

/**
 * The message queue + log — the Reminders page's Messages tab. With a
 * `reminderId` it shows only the messages that one reminder queued.
 */
export function WhatsAppMessagesTab({ reminderId }: { reminderId?: string }) {
  const db = useDb()
  const result = useLiveQuery((q) =>
    q.from({ messages: db.whatsappMessagesCollection }),
  )
  const customersResult = useLiveQuery((q) =>
    q.from({ customers: db.customersCollection }),
  )
  const settingsResult = useLiveQuery((q) =>
    q.from({ settings: db.appSettingsCollection }),
  )

  useEffect(() => {
    void db.appSettingsCollection.preload?.()
  }, [db])

  const sendingEnabled =
    settingsResult.data?.[0]?.whatsappSendingEnabled ?? false
  const allMessages = result.data ?? []
  const messages = reminderId
    ? allMessages.filter((message) => message.reminderId === reminderId)
    : allMessages
  const hasPending = messages.some((message) => message.status === 'Queued')
  const [now, setNow] = useState(() => new Date().toISOString())

  // Tick once a second while messages are counting down their grace period,
  // so the "Sends in Ns" badges and cancel buttons stay honest.
  useEffect(() => {
    if (!hasPending) return
    const interval = setInterval(
      () => setNow(new Date().toISOString()),
      1000,
    )
    return () => clearInterval(interval)
  }, [hasPending])

  if (result.isLoading) {
    return <p className="text-sm text-slate-600">Loading messages…</p>
  }

  if (result.isError) {
    return (
      <p className="text-sm text-slate-600">
        Unable to load messages right now.
      </p>
    )
  }

  const customerFor = (customerId?: string) =>
    customerId
      ? customersResult.data?.find((customer) => customer.id === customerId)
      : undefined

  const rows: WhatsAppMessageRow[] = messages.map((message) => {
    const customer = customerFor(message.customerId)
    return {
      id: message.id,
      toPhone: message.toPhone,
      customerName: customer?.fullName,
      customerId: message.customerId,
      customerPublicId: customer?.publicId,
      serviceId: message.serviceId,
      servicePublicId: message.servicePublicId,
      content: message.content,
      status: message.status,
      failureReason: message.failureReason,
      sentAtLabel: message.sentAt
        ? `${toZaDateKey(message.sentAt)} ${toZaTime(message.sentAt)}`
        : undefined,
      sortTimestamp: message.sentAt ?? message.updatedAt,
      graceSecondsRemaining: Math.ceil(graceRemainingMs(message, now) / 1000),
      cancellable: isCancellable(message, now),
    }
  })

  return (
    <div className="space-y-3">
      <WhatsAppScreen
        rows={rows}
        sendingEnabled={sendingEnabled}
        onCancelMessage={(id) => db.cancelWhatsAppMessage(id)}
        onResendMessage={(id) => db.resendWhatsAppMessage(id)}
      />
    </div>
  )
}
